import type { AgentSessionViewModel } from '@inf-monkeys-tech/monkeys';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Circle,
  Clock3,
  Copy,
  Download,
  FileCode2,
  FileOutput,
  Files,
  GitFork,
  PencilLine,
  RefreshCw,
  RotateCcw,
  TerminalSquare,
  TestTube2,
  X,
  XCircle,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';

import { useMonkeysLocale, useMonkeysLocaleMessages } from '../../provider';
import { cn } from '../layout';
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '../primitives';
import {
  createAgentWorkbenchDetailsModel,
  type AgentWorkbenchDetailsModel,
} from './details-model';

export type AgentWorkbenchDetailsTab = 'summary' | 'workspace';
export type AgentWorkbenchDetailsState =
  | 'ready'
  | 'loading'
  | 'empty'
  | 'error';

export type AgentWorkbenchDetailsIntent =
  | { type: 'close' }
  | { type: 'copy-summary'; summaryId: string; text: string }
  | { type: 'retry'; eventId: string }
  | { type: 'retry-summary'; eventId: string }
  | { type: 'resume'; sessionId: string }
  | { type: 'continue-in-new-task'; sessionId: string }
  | { type: 'edit-and-rerun'; sessionId: string }
  | { type: 'export'; sessionId: string };

export interface AgentWorkbenchTaskDetailsClassNames {
  root?: string;
  header?: string;
  tabs?: string;
  body?: string;
  section?: string;
  code?: string;
}

export interface AgentWorkbenchTaskDetailsProps {
  viewModel?: AgentSessionViewModel;
  state?: AgentWorkbenchDetailsState;
  activeTab?: AgentWorkbenchDetailsTab;
  className?: string;
  classNames?: AgentWorkbenchTaskDetailsClassNames;
  onActiveTabChange?: (tab: AgentWorkbenchDetailsTab) => void;
  onIntent?: (intent: AgentWorkbenchDetailsIntent) => void;
  onExport?: (intent: Extract<AgentWorkbenchDetailsIntent, { type: 'export' }>) => void;
  onCopySummary?: (intent: Extract<AgentWorkbenchDetailsIntent, { type: 'copy-summary' }>) => void;
  onRetry?: (intent: Extract<AgentWorkbenchDetailsIntent, { type: 'retry' }>) => void;
  onRetrySummary?: (intent: Extract<AgentWorkbenchDetailsIntent, { type: 'retry-summary' }>) => void;
}

function formatDuration(durationMs: number | undefined): string {
  if (durationMs === undefined) return '-';
  const seconds = Math.max(0, Math.round(durationMs / 1_000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function formatDate(value: string | undefined, locale: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function Count({ value }: { value: number }) {
  return (
    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-foreground">
      {value}
    </span>
  );
}

function DetailsSection({
  title,
  count,
  icon: Icon,
  className,
  children,
}: {
  title: string;
  count: number;
  icon: ComponentType<{ className?: string }>;
  className?: string;
  children: ReactNode;
}) {
  if (count === 0) return null;
  return (
    <details
      className={cn(
        'group rounded-lg border border-border bg-card text-card-foreground',
        className,
      )}
      open
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate">{title}</span>
        <Count value={count} />
        <ChevronDown
          className="size-4 text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <div className="border-t border-border p-3">{children}</div>
    </details>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'completed' || status === 'passed') {
    return <CheckCircle2 className="size-4 text-primary" aria-hidden="true" />;
  }
  if (status === 'failed') {
    return <XCircle className="size-4 text-destructive-text" aria-hidden="true" />;
  }
  if (status === 'running') {
    return <Clock3 className="size-4 animate-pulse" aria-hidden="true" />;
  }
  return <Circle className="size-4 text-muted-foreground" aria-hidden="true" />;
}

function StateContent({ state }: { state: Exclude<AgentWorkbenchDetailsState, 'ready'> }) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.details;
  const error = state === 'error';
  return (
    <div
      className="flex min-h-48 flex-col items-center justify-center gap-2 px-6 text-center"
      role={error ? 'alert' : state === 'loading' ? 'status' : undefined}
      aria-busy={state === 'loading' || undefined}
    >
      {state === 'loading' ? (
        <RefreshCw className="size-5 animate-spin text-muted-foreground" aria-hidden="true" />
      ) : error ? (
        <AlertCircle className="size-5 text-destructive-text" aria-hidden="true" />
      ) : (
        <FileOutput className="size-5 text-muted-foreground" aria-hidden="true" />
      )}
      <div className="text-sm font-medium">{messages.state[state].title}</div>
      <div className="text-xs text-muted-foreground">
        {messages.state[state].description}
      </div>
    </div>
  );
}

function SummaryPanel({
  model,
  onIntent,
}: {
  model: AgentWorkbenchDetailsModel;
  onIntent?: (intent: AgentWorkbenchDetailsIntent) => void;
}) {
  const workbenchMessages = useMonkeysLocaleMessages().agentWorkbench;
  const messages = workbenchMessages.details;
  const locale = useMonkeysLocale().locale;
  const summary = model.summary;
  const run = model.run?.payload;
  const error = model.error;
  const retryable = model.status === 'failed' && error?.payload.retryable;
  const canResume =
    model.resumable && ['stopped', 'completed', 'failed'].includes(model.status);
  const canContinueInNewTask = model.capabilities.threadForking;
  const canEditAndRerun = model.capabilities.editAndRerun;
  const tokenUsage =
    model.usage?.payload.totalTokens ??
    (model.usage?.payload.inputTokens !== undefined ||
    model.usage?.payload.outputTokens !== undefined
      ? (model.usage.payload.inputTokens ?? 0) +
        (model.usage.payload.outputTokens ?? 0)
      : undefined);

  return (
    <div className="space-y-3">
      <section className="rounded-lg border border-border bg-muted/40 p-3">
        <div className="flex items-center gap-2">
          <StatusIcon status={model.status} />
          <span className="text-sm font-semibold">
            {messages.status[model.status]}
          </span>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
          <div>
            <dt className="text-muted-foreground">{messages.duration}</dt>
            <dd className="mt-0.5 font-medium">
              {formatDuration(run?.durationMs)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">{messages.updated}</dt>
            <dd className="mt-0.5 truncate font-medium">
              {formatDate(
                run?.stoppedAt ?? run?.completedAt ?? run?.failedAt,
                locale,
              )}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">{messages.model}</dt>
            <dd className="mt-0.5 truncate font-medium">{model.modelId}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">{messages.mode}</dt>
            <dd className="mt-0.5 font-medium">
              {workbenchMessages.modes[model.mode]}
            </dd>
          </div>
          {model.permissionProfile ? (
            <div>
              <dt className="text-muted-foreground">{messages.permissions}</dt>
              <dd className="mt-0.5 font-medium">
                {workbenchMessages.composer.permissionOptions[model.permissionProfile]}
              </dd>
            </div>
          ) : null}
          {tokenUsage !== undefined ? (
            <div>
              <dt className="text-muted-foreground">{messages.tokenUsage}</dt>
              <dd className="mt-0.5 font-medium">
                {new Intl.NumberFormat(locale).format(tokenUsage)}
              </dd>
            </div>
          ) : null}
        </dl>
        {error ? (
          <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 p-2.5 text-sm text-destructive-text" role="alert">
            {error.payload.message}
          </div>
        ) : null}
        {retryable || canResume ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {retryable ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-auto px-2.5 py-1.5 text-xs"
                disabled={!onIntent}
                onClick={() => onIntent?.({ type: 'retry', eventId: error.eventId })}
              >
                {messages.actions.retry}
              </Button>
            ) : null}
            {canResume ? (
              <Button
                type="button"
                size="sm"
                className="h-auto px-2.5 py-1.5 text-xs"
                disabled={!onIntent}
                onClick={() =>
                  onIntent?.({ type: 'resume', sessionId: model.sessionId })
                }
              >
                {messages.actions.resume}
              </Button>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="flex flex-wrap gap-2 rounded-lg border border-border bg-card p-3 text-card-foreground">
        {canContinueInNewTask ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-auto gap-1.5 px-2.5 py-1.5 text-xs"
            disabled={!onIntent}
            onClick={() => onIntent?.({ type: 'continue-in-new-task', sessionId: model.sessionId })}
          >
            <GitFork className="size-3.5" aria-hidden="true" />
            {messages.actions.continueInNewTask}
          </Button>
        ) : null}
        {canEditAndRerun ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-auto gap-1.5 px-2.5 py-1.5 text-xs"
            disabled={!onIntent}
            onClick={() => onIntent?.({ type: 'edit-and-rerun', sessionId: model.sessionId })}
          >
            <PencilLine className="size-3.5" aria-hidden="true" />
            {messages.actions.editAndRerun}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-auto gap-1.5 px-2.5 py-1.5 text-xs"
          disabled={!onIntent}
          onClick={() => onIntent?.({ type: 'export', sessionId: model.sessionId })}
        >
          <Download className="size-3.5" aria-hidden="true" />
          {messages.actions.export}
        </Button>
      </section>

      <section className="rounded-lg border border-border bg-card p-3 text-card-foreground">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">{messages.summary}</h3>
          {summary?.payload.text ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 text-muted-foreground hover:text-foreground"
              aria-label={messages.actions.copySummary}
              disabled={!onIntent}
              onClick={() =>
                onIntent?.({
                  type: 'copy-summary',
                  summaryId: summary.payload.summaryId,
                  text: summary.payload.text,
                })
              }
            >
              <Copy className="size-4" aria-hidden="true" />
            </Button>
          ) : null}
        </div>
        {summary?.payload.status === 'failed' ? (
          <div className="mt-3 space-y-2">
            <p className="text-sm text-destructive-text" role="alert">
              {messages.summaryFailed}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-auto gap-1.5 px-2.5 py-1.5 text-xs"
              disabled={!onIntent}
              onClick={() =>
                onIntent?.({ type: 'retry-summary', eventId: summary.eventId })
              }
            >
              <RotateCcw className="size-3.5" aria-hidden="true" />
              {messages.actions.retrySummary}
            </Button>
          </div>
        ) : summary?.payload.text ? (
          <div className="mt-3 space-y-3 text-sm">
            <p className="whitespace-pre-wrap text-muted-foreground">
              {summary.payload.text}
            </p>
            {summary.payload.highlights.length > 0 ? (
              <div>
                <h4 className="text-xs font-medium">{messages.highlights}</h4>
                <ul className="mt-1 list-disc space-y-1 pl-5 text-muted-foreground">
                  {summary.payload.highlights.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {summary.payload.pendingItems.length > 0 ? (
              <div>
                <h4 className="text-xs font-medium">{messages.pendingItems}</h4>
                <ul className="mt-1 list-disc space-y-1 pl-5 text-muted-foreground">
                  {summary.payload.pendingItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">
            {messages.summaryEmpty}
          </p>
        )}
      </section>
    </div>
  );
}

function WorkspacePanel({
  model,
  sectionClassName,
  codeClassName,
}: {
  model: AgentWorkbenchDetailsModel;
  sectionClassName?: string;
  codeClassName?: string;
}) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.details.workspace;
  const workspace = model.workspace;
  return (
    <div className="space-y-3">
      <DetailsSection
        title={messages.diffs}
        count={workspace.diffs.length}
        icon={FileCode2}
        className={sectionClassName}
      >
        <div className="space-y-3">
          {workspace.diffs.map((event) => (
            <div key={event.payload.path} className="min-w-0">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate font-medium">{event.payload.path}</span>
                <span className="shrink-0 text-muted-foreground">
                  {messages.diffStatus[event.payload.status]}
                </span>
              </div>
              {event.payload.patch ? (
                <pre className={cn('mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 font-mono text-xs', codeClassName)}>
                  {event.payload.patch}
                </pre>
              ) : null}
            </div>
          ))}
        </div>
      </DetailsSection>

      <DetailsSection
        title={messages.files}
        count={workspace.files.length}
        icon={Files}
        className={sectionClassName}
      >
        <ul className="space-y-2 text-xs">
          {workspace.files.map((event) => (
            <li key={event.payload.path} className="flex items-center gap-2">
              <FileOutput className="size-3.5 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{event.payload.path}</span>
              <span className="text-muted-foreground">
                {messages.fileOperation[event.payload.operation]}
              </span>
            </li>
          ))}
        </ul>
      </DetailsSection>

      <DetailsSection
        title={messages.terminals}
        count={workspace.terminals.length}
        icon={TerminalSquare}
        className={sectionClassName}
      >
        <div className="space-y-3">
          {workspace.terminals.map((terminal) => (
            <div key={terminal.terminalId}>
              <div className="flex justify-between gap-2 text-xs font-medium">
                <span>{terminal.terminalId}</span>
                <span className="text-muted-foreground">
                  {terminal.exitCode === undefined
                    ? terminal.status
                    : messages.exitCode({ code: terminal.exitCode })}
                </span>
              </div>
              <pre className={cn('mt-2 max-h-72 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 font-mono text-xs', codeClassName)}>
                {[terminal.stdin, terminal.stdout, terminal.stderr]
                  .filter(Boolean)
                  .join('\n')}
              </pre>
            </div>
          ))}
        </div>
      </DetailsSection>

      <DetailsSection
        title={messages.tests}
        count={workspace.tests.length}
        icon={TestTube2}
        className={sectionClassName}
      >
        <ul className="space-y-2 text-xs">
          {workspace.tests.map((event) => (
            <li key={event.payload.testId}>
              <div className="flex items-center gap-2">
                <StatusIcon status={event.payload.status} />
                <span className="min-w-0 flex-1 truncate">{event.payload.name}</span>
                {event.payload.durationMs === undefined ? null : (
                  <span className="text-muted-foreground">
                    {formatDuration(event.payload.durationMs)}
                  </span>
                )}
              </div>
              {event.payload.output ? (
                <pre className={cn('mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 font-mono text-xs', codeClassName)}>
                  {event.payload.output}
                </pre>
              ) : null}
            </li>
          ))}
        </ul>
      </DetailsSection>

      <DetailsSection
        title={messages.artifacts}
        count={workspace.artifacts.length}
        icon={FileOutput}
        className={sectionClassName}
      >
        <ul className="space-y-2 text-xs">
          {workspace.artifacts.map((event) => (
            <li key={event.payload.artifactId} className="flex items-center gap-2">
              <FileOutput className="size-3.5 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{event.payload.name}</span>
              <span className="text-muted-foreground">
                {event.payload.kind}
              </span>
            </li>
          ))}
        </ul>
      </DetailsSection>
    </div>
  );
}

export function AgentWorkbenchTaskDetails({
  viewModel,
  state = 'ready',
  activeTab = 'summary',
  className,
  classNames,
  onActiveTabChange,
  onIntent,
  onExport,
  onCopySummary,
  onRetry,
  onRetrySummary,
}: AgentWorkbenchTaskDetailsProps) {
  const dispatch = onIntent || onExport || onCopySummary || onRetry || onRetrySummary ? (intent: AgentWorkbenchDetailsIntent) => {
    if (intent.type === 'export' && onExport) return onExport(intent);
    if (intent.type === 'copy-summary' && onCopySummary) return onCopySummary(intent);
    if (intent.type === 'retry' && onRetry) return onRetry(intent);
    if (intent.type === 'retry-summary' && onRetrySummary) return onRetrySummary(intent);
    onIntent?.(intent);
  } : undefined;
  const messages = useMonkeysLocaleMessages().agentWorkbench.details;
  if (state !== 'ready') {
    return (
      <aside className={cn('flex h-full min-h-0 flex-col border-l border-border bg-background', classNames?.root, className)}>
        <StateContent state={state} />
      </aside>
    );
  }
  if (!viewModel) {
    return (
      <aside className={cn('flex h-full min-h-0 flex-col border-l border-border bg-background', classNames?.root, className)}>
        <StateContent state="empty" />
      </aside>
    );
  }

  const model = createAgentWorkbenchDetailsModel(viewModel);
  const workspaceCount = Object.values(model.workspace).reduce(
    (total, items) => total + items.length,
    0,
  );
  const workspaceAvailable = workspaceCount > 0;
  const resolvedTab = activeTab === 'workspace' && !workspaceAvailable ? 'summary' : activeTab;
  const tabs: Array<{ id: AgentWorkbenchDetailsTab; label: string; count: number }> = [
    { id: 'summary', label: messages.tabs.summary, count: model.summary ? 1 : 0 },
    ...(workspaceAvailable
      ? [{ id: 'workspace' as const, label: messages.tabs.workspace, count: workspaceCount }]
      : []),
  ];

  return (
    <aside
      className={cn(
        'flex h-full min-h-0 min-w-0 flex-col border-l border-border bg-background text-foreground',
        classNames?.root,
        className,
      )}
      data-agent-workbench-mode={model.mode}
    >
      <header className={cn('flex shrink-0 items-center gap-2 border-b border-border px-4 py-3', classNames?.header)}>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold">{messages.title}</h2>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {messages.description}
          </p>
        </div>
        {onIntent ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground"
            aria-label={messages.actions.close}
            onClick={() => onIntent({ type: 'close' })}
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
        ) : null}
      </header>

      <Tabs
        value={resolvedTab}
        className="flex min-h-0 flex-1 flex-col"
        onValueChange={(tab) => onActiveTabChange?.(tab as AgentWorkbenchDetailsTab)}
      >
        <TabsList
          aria-label={messages.tabs.label}
          className={cn(
            'flex h-auto shrink-0 gap-1 rounded-none border-b border-border bg-transparent px-3 py-2',
            classNames?.tabs,
          )}
        >
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className="min-w-0 flex-1 gap-1 px-2 py-1.5 text-xs shadow-none data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none"
            >
              <span className="truncate">{tab.label}</span>
              <Count value={tab.count} />
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value="summary"
          className={cn('mt-0 min-h-0 flex-1 overflow-y-auto p-3', classNames?.body)}
        >
          <SummaryPanel model={model} onIntent={dispatch} />
        </TabsContent>
        {workspaceAvailable ? (
          <TabsContent
            value="workspace"
            className={cn('mt-0 min-h-0 flex-1 overflow-y-auto p-3', classNames?.body)}
          >
            <WorkspacePanel
              model={model}
              sectionClassName={classNames?.section}
              codeClassName={classNames?.code}
            />
          </TabsContent>
        ) : null}
      </Tabs>
    </aside>
  );
}
